/**
 * Public staff co-sign page.
 *
 * Staff open this from the email link. They enter their 4-digit PIN, then
 * get redirected to the DocuSeal embed to co-sign the consent document.
 *
 * URL shape: /consent/staff-sign?cr=<consent_record_id>
 * After signing DocuSeal redirects to:  /consent/staff-sign?cr=<id>&signed=1
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { consent } from '@/api/client';
import { SigningProgress } from '@/pages/subjects/onboarding/SigningProgress';

const PIN_LEN = 4;

export function StaffConsentPage() {
  const [params] = useSearchParams();
  const consentRecordId = params.get('cr') ?? '';

  const embedKey = `staff_consent_embed_${consentRecordId}`;

  const [digits, setDigits] = useState<string[]>(Array(PIN_LEN).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(
    () => (consentRecordId ? sessionStorage.getItem(embedKey) : null),
  );
  const [autoRedirect, setAutoRedirect] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState<boolean | null>(null);

  const pin = digits.join('');
  const ready = pin.length === PIN_LEN && /^\d+$/.test(pin) && !!consentRecordId;

  // Check if staff has already signed on mount.
  useEffect(() => {
    if (!consentRecordId || params.get('signed') === '1') return;
    consent.remoteStatus(consentRecordId, 'staff')
      .then((r) => {
        if (r.signed) {
          sessionStorage.removeItem(embedKey);
          setAlreadySigned(true);
        } else {
          setAlreadySigned(false);
        }
      })
      .catch(() => setAlreadySigned(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentRecordId]);

  async function submit() {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await consent.verifyStaffPin({ consent_record_id: consentRecordId, pin });
      if (!r.embed_url) {
        setError('Signing session not ready — contact the site coordinator.');
        setDigits(Array(PIN_LEN).fill(''));
        return;
      }
      sessionStorage.setItem(embedKey, r.embed_url);
      setAutoRedirect(true);
      setEmbedUrl(r.embed_url);
    } catch (e: unknown) {
      const err = e as { status?: number; code?: string; message?: string };
      if (err.status === 409) {
        sessionStorage.removeItem(embedKey);
        setAlreadySigned(true);
        return;
      }
      if (err.code === 'PIN_EXPIRED') {
        setError('Your PIN has expired. Contact the coordinator to send a new one.');
      } else if (err.code === 'PIN_CONSUMED') {
        setError('Your PIN was already used. If you haven\'t finished signing, open the co-sign form directly from the link email.');
      } else if (err.status === 423) {
        setError('Too many failed attempts. Contact the site coordinator.');
      } else {
        setError('Incorrect PIN. Please check the email and try again.');
      }
      setDigits(Array(PIN_LEN).fill(''));
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (ready) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  useEffect(() => {
    if (embedUrl && autoRedirect) window.location.href = embedUrl;
  }, [embedUrl, autoRedirect]);

  if (!consentRecordId) {
    return (
      <Shell>
        <p className="text-sm text-ink-muted">
          This link is missing the consent reference. Open it from the email message exactly as it
          arrived.
        </p>
      </Shell>
    );
  }

  // Staff returned from DocuSeal after co-signing (?signed=1).
  if (params.get('signed') === '1') {
    sessionStorage.removeItem(embedKey);
    return (
      <Shell>
        <SigningProgress
          stages={{ identify: 'completed', sign: 'completed', confirmed: 'completed' }}
          labels={{ identify: 'Verified', sign: 'Co-signed', confirmed: 'Confirmed' }}
        />
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <ShieldCheck className="h-10 w-10 text-accent-600" />
          <p className="text-base font-semibold text-ink-primary">Co-signature recorded</p>
          <p className="text-xs text-ink-muted">
            Thank you. Your co-signature has been recorded. You may close this window.
          </p>
        </div>
      </Shell>
    );
  }

  // Consent already fully signed.
  if (alreadySigned === true) {
    return (
      <Shell>
        <SigningProgress
          stages={{ identify: 'completed', sign: 'completed', confirmed: 'completed' }}
          labels={{ identify: 'Verified', sign: 'Co-signed', confirmed: 'Confirmed' }}
        />
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <ShieldCheck className="h-10 w-10 text-accent-600" />
          <p className="text-base font-semibold text-ink-primary">Consent already completed</p>
          <p className="text-xs text-ink-muted">
            This consent form has already been fully signed. You may close this window.
          </p>
        </div>
      </Shell>
    );
  }

  if (embedUrl && autoRedirect) return null;

  // Staff came back without finishing — resume link.
  if (embedUrl) {
    return (
      <Shell>
        <SigningProgress
          stages={{ identify: 'completed', sign: 'active', confirmed: 'pending' }}
          labels={{ identify: 'Verified', sign: 'Co-sign', confirmed: 'Confirmed' }}
        />
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <p className="text-sm font-medium text-ink-primary">Your PIN has already been verified.</p>
          <p className="text-xs text-ink-muted">
            Click below to open the consent form and complete your co-signature.
          </p>
          <button
            type="button"
            onClick={() => { window.location.href = embedUrl; }}
            className="mt-2 w-full rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            Open co-sign form
          </button>
        </div>
      </Shell>
    );
  }

  if (alreadySigned === null) return null;

  return (
    <Shell>
      <SigningProgress
        stages={{ identify: 'completed', sign: 'active', confirmed: 'pending' }}
        labels={{ identify: 'Verified', sign: 'Co-sign', confirmed: 'Confirmed' }}
      />
      <h1 className="mt-6 text-center text-base font-semibold text-ink-primary">Enter your PIN</h1>
      <p className="mt-1 text-center text-xs text-ink-muted">
        Enter the 4-digit PIN from the PIN email
      </p>

      <div className="mt-6 flex justify-center gap-2">
        {digits.map((d, i) => (
          <input
            key={i}
            id={`staff-pin-${i}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            disabled={submitting}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(-1);
              setDigits((prev) => {
                const next = [...prev];
                next[i] = v;
                return next;
              });
              if (v && i < PIN_LEN - 1) {
                const nextEl = document.getElementById(`staff-pin-${i + 1}`) as HTMLInputElement | null;
                nextEl?.focus();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digits[i] && i > 0) {
                const prevEl = document.getElementById(`staff-pin-${i - 1}`) as HTMLInputElement | null;
                prevEl?.focus();
              }
            }}
            className={`h-14 w-12 rounded border text-center text-2xl font-semibold ${
              error ? 'border-danger-400 text-danger-700' : 'border-surface-border text-ink-primary'
            }`}
          />
        ))}
      </div>

      {error && <p className="mt-3 text-center text-xs text-danger-600">{error}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={!ready || submitting}
        className="mt-6 w-full rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
      >
        {submitting ? 'Verifying…' : 'Access co-sign form'}
      </button>

      <p className="mt-6 text-center text-[11px] text-ink-muted">
        Didn't receive a PIN? Contact the site coordinator listed in the link email.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface-base">
      <div className="mx-auto max-w-2xl px-4 py-8">
        <div className="mb-6 text-center">
          <div className="text-lg font-semibold text-ink-primary">DreamTrials</div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted">
            Informed Consent — Staff Co-signature
          </div>
        </div>
        <div className="rounded border border-surface-border bg-surface-card p-6 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  );
}
